// packages import
import { useNavigate } from 'react-router-dom'
import { Box, FormControl, InputLabel, Select, MenuItem, TextField, Button, CircularProgress } from '@mui/material'
import { FC, useContext, useEffect } from 'react'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { endOfDay } from 'date-fns'

// component imports
import { Alert } from '../../common/Alert'
import AttorneyDropdown from '../../common/AttorneyDropdown'
import ClientDropdown from '../../common/ClientDropdown'

// others
import {
  BillingCycle,
  Maybe,
  User,
  ExternalUser,
  FetchBillableCasesInput,
  BillableCasesByType,
  useGeneratePreBillsMutation,
  BillType
} from '../../../../generated'
import { flexCenter } from '../../../theme/styleConstants'
import { Billing_Cycle_Options } from '../../../constants'
import { AuthContext } from '../../../context'

interface GenerateBillsFiltersProps {
  filters: FetchBillableCasesInput
  type: 'attorney' | 'client'
  selectedCases: string[]
  billableCases: BillableCasesByType | null
  setFilters: (filters: Partial<FetchBillableCasesInput>) => void
}

const GenerateBillsHeader: FC<GenerateBillsFiltersProps> = ({
  filters,
  type,
  selectedCases,
  billableCases,
  setFilters
}) => {
  const navigate = useNavigate()
  const { isAttorney, currentUser } = useContext(AuthContext)
  const [generatePreBills, { loading: generatingPreBills }] = useGeneratePreBillsMutation()

  const attorneyId = filters.attorneyId || undefined
  const clientId = filters.clientId || undefined
  const billingCycle = filters.billingCycle || BillingCycle.EndOfMonth
  const customDate = filters.customDate ? new Date(filters.customDate) : null

  const handleAttorneyChange = (attorney: Maybe<User>) => {
    setFilters({
      attorneyId: attorney?.id,
      clientId: undefined
    })
  }

  const handleClientChange = (client: Maybe<ExternalUser>) => {
    setFilters({
      clientId: client?.id,
      attorneyId: undefined
    })
  }

  const handleBillingCycleChange = (billingCycle: BillingCycle) => {
    setFilters({ billingCycle })
    if (billingCycle !== BillingCycle.CustomDate) {
      setFilters({ customDate: undefined })
    }
  }

  const handleCustomDateChange = (date: Date | null) => {
    if (date) {
      setFilters({ customDate: endOfDay(new Date(date)).toISOString() })
    } else {
      setFilters({ customDate: undefined })
    }
  }

  const handleGenerateAndSendForApproval = async () => {
    await handleGeneratePreBills(true)
  }

  const prepareBillData = () => {
    if (!billableCases || selectedCases.length === 0) return []

    const selectedCasesMap = new Map(selectedCases.map(caseId => [caseId, true]))
    const allCases = [
      ...(billableCases.flatFee || []),
      ...(billableCases.hourly || []),
      ...(billableCases.contingency || []),
      ...(billableCases.hybrid || []),
      ...(billableCases.contingencyHybrid || []),
      ...(billableCases.proBono || []),
      ...(billableCases.consult || [])
    ]

    const selectedCaseData = allCases.filter(caseItem => selectedCasesMap.has(caseItem.caseId))

    return selectedCaseData.map(caseItem => ({
      caseId: caseItem.caseId,
      type: BillType.Bill,
      amount: caseItem.totalAmount,
      discount: caseItem.discount,
      payOffAmount: caseItem?.payoffAmount,
      items: caseItem.entries.map(entry => ({
        entryId: entry.id,
        amount: entry.amount
      }))
    }))
  }

  const handleGeneratePreBills = async (alsoSendForApproval?: boolean) => {
    if (selectedCases.length === 0) {
      Alert.error('Please select at least one case to generate pre-bills')
      return
    }

    try {
      const billData = prepareBillData()
      if (billData.length === 0) {
        Alert.error('No valid bill data found for selected cases')
        return
      }

      const { data } = await generatePreBills({
        variables: { generatePreBillsInput: billData, alsoSendForAttorneyApproval: alsoSendForApproval }
      })

      if (data?.generatePreBills?.response?.status === 201) {
        Alert.success(
          `Successfully generated ${data.generatePreBills.billIds.length} pre-bill(s) ${
            alsoSendForApproval ? 'and sent for attorney approval' : ''
          }`
        )
        navigate('/billing/pre-bills')
      }
    } catch (error: any) {
      Alert.error(error.message || 'Failed to generate pre-bills')
      console.error('Generate pre-bills error:', error)
    }
  }

  useEffect(() => {
    if (isAttorney) {
      setFilters({ attorneyId: currentUser?.id })
    }

    // setFilters excluded to prevent infinite re-renders due to changing function reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAttorney, currentUser])

  return (
    <Box
      sx={{
        px: 3,
        py: 2,
        borderBottom: '1px solid #e0e0e0',
        gap: 1,
        flexWrap: 'wrap',
        ...flexCenter
      }}
    >
      {type === 'attorney' ? (
        !isAttorney && (
          <AttorneyDropdown
            selectedAttorneyId={attorneyId}
            label="Select Attorney"
            sx={{ minWidth: 400 }}
            onChange={handleAttorneyChange}
          />
        )
      ) : (
        <ClientDropdown
          selectedClientId={clientId}
          label="Select Client"
          sx={{ minWidth: 200 }}
          onChange={handleClientChange}
        />
      )}

      <FormControl sx={{ minWidth: 180 }}>
        <InputLabel>Billing Cycle</InputLabel>
        <Select
          value={filters.billingCycle}
          label="Billing Cycle"
          onChange={e => handleBillingCycleChange(e.target.value as BillingCycle)}
        >
          {Billing_Cycle_Options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {billingCycle === BillingCycle.CustomDate && (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Custom Date"
            value={customDate}
            onChange={handleCustomDateChange}
            renderInput={params => <TextField {...params} sx={{ minWidth: 200 }} />}
          />
        </LocalizationProvider>
      )}

      <Box sx={{ ml: 'auto', gap: 2, ...flexCenter }}>
        <Button
          variant="outlined"
          onClick={handleGenerateAndSendForApproval}
          disabled={selectedCases.length === 0 || generatingPreBills}
        >
          GENERATE & SEND FOR APPROVAL
        </Button>

        <Button
          variant="contained"
          onClick={() => handleGeneratePreBills(false)}
          disabled={selectedCases.length === 0 || generatingPreBills}
          endIcon={generatingPreBills ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          GENERATE PRE BILLS {selectedCases.length > 0 && `(${selectedCases.length})`}
        </Button>
      </Box>
    </Box>
  )
}

export default GenerateBillsHeader
